'use client';

// Core
import { useEffect, useState } from 'react';
// Vendors
import { useTranslations } from '@/hooks/useTranslations';
import { Check, ChevronsUpDown } from 'lucide-react';
// Libs
import { cn } from '@/lib/utils';
// UI components
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
// Global types
import { IClient } from '@/types/client';
import { useClientContext } from '@/contexts/client-context';
import { getClients } from '@/actions/client/client-queries';

interface IChoseClient {
  value?: string;
  onSelect: (value: string) => void;
  placeholder: string;
}

export function SelectClient({ value, onSelect, placeholder }: IChoseClient) {
  const t = useTranslations('CreateClientPopup');

  const { dispatch, state: clientState } = useClientContext();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<IClient[]>([]);
  const [prevPopupOpen, setPrevPopupOpen] = useState(false);

  const fetchClients = async () => {
    try {
      const data = await getClients();
      setClients(data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Refetch clients when create popup closes (new client may have been added)
  useEffect(() => {
    if (prevPopupOpen && !clientState.isCreateClientPopupOpen) {
      fetchClients();
    }
    setPrevPopupOpen(clientState.isCreateClientPopupOpen);
  }, [clientState.isCreateClientPopupOpen]);

  const selectedClientName =
    value && clients.find((client) => client.id === value)?.name;

  if (loading)
    return (
      <div className="flex items-center justify-center p-2">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          aria-label="Select client"
        >
          {selectedClientName || placeholder}

          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="p-0">
        <Command>
          <CommandInput placeholder={t('searchPlaceholder')} />

          <CommandList>
            <CommandEmpty>{t('noResults')}</CommandEmpty>

            <CommandGroup>
              {clients.map((client) => (
                <CommandItem
                  className="cursor-pointer"
                  key={client.id}
                  value={client.name}
                  onSelect={() => {
                    onSelect(client.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === client.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />

                  {client.name}
                </CommandItem>
              ))}
            </CommandGroup>

            <div className="text-gray-500 text-center">or</div>

            <CommandGroup>
              <CommandItem value="add new">
                <Button
                  aria-label="Add new client"
                  variant="ghost"
                  className="w-full border"
                  onClick={() => dispatch({ type: 'OPEN_CREATE_CLIENT_POPUP' })}
                >
                  {t('addNewClient')}
                </Button>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
